/**
 * Le dossier tel que le journal daté le soutient — la moitié temporelle de la page.
 *
 * Deux lectures, et elles ne répondent pas à la même question : `/state` dit ce qui était vrai ce
 * jour-là (les valeurs, leur date clinique, l'univers des champs), `/pathway` dit ce qui s'est
 * passé (épisodes, moments, ontologie accrochée à chaque moment). Elles vont ensemble parce
 * qu'aucun écran n'affiche l'une sans l'autre.
 *
 * Extrait de `routes/patient` pour une raison précise : la modale du jeu de données est
 * censée être le reflet de la fiche patient, et elle ne pouvait le devenir qu'en refaisant ces deux
 * appels à la main. Deux lectures parallèles du même journal auraient fini par diverger sur le cas
 * qui compte — l'échec, que la page DOIT dire au lieu d'afficher un dossier vide.
 *
 * Le parcours n'est PAS rechargé quand le curseur bouge : l'objet est petit et entièrement daté,
 * donc il est rejoué localement (`projectPathway`). Un aller-retour par cran ferait du curseur un
 * formulaire.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import API from "@app/services/api";
import type { PathwayResponse } from "@app/utils/care-pathway";
import type { ServedGesture } from "@app/utils/resolve-field-value";

/**
 * Un jour clinique (AAAA-MM-JJ), ou `null` si ce n'en est pas un.
 *
 * La date du curseur arrive d'endroits qui ne la garantissent pas : la barre d'adresse, où
 * n'importe qui peut écrire n'importe quoi, et le `as_of` d'une ligne de jeu de données, sérialisé
 * par le serveur. Les faire passer par le même filtre évite qu'un `?at=demain` parte tel quel dans
 * une requête de projection.
 */
export function asClinicalDay(value: unknown): string | null {
  if (typeof value !== "string" || value.trim() === "") return null;
  const day = dayjs(value);
  return day.isValid() ? day.format("YYYY-MM-DD") : null;
}

/** A passage a projected value rests on (`GET /state` → `field_evidence`). */
export interface FieldEvidence {
  value?: unknown;
  evidence: string;
  source_doc_id?: string | null;
  doc_date?: string | null;
  doc_type?: string | null;
}

export interface TemporalProjection {
  /** Valeurs projetées à la date du curseur, ou `null` si la lecture a échoué. */
  fields: Record<string, unknown> | null;
  /** `field_key` → date clinique de la valeur affichée (annotation « au DATE »). */
  dates: Record<string, string>;
  /** `field_key` → the passages behind the value shown, most recent first. */
  evidence: Record<string, FieldEvidence[]>;
  /** `field_key` → the clinician's gesture beside the value shown, decided by the server with it. */
  gestures: Record<string, ServedGesture>;
  /** L'univers clinique : tous les champs d'ontologie extraits, vidés puis re-superposés. */
  clinicalKeys: string[];
  /**
   * La projection n'a pas pu être lue. Tout le clinique de l'écran en vient, donc sans ce drapeau
   * le dossier s'affiche vide et « Renseignés · 0 » se lit comme un constat clinique.
   */
  failed: boolean;
  /** Quand la chaîne doc-first a lu ce dossier (`PatientDossierReading.read_at`). */
  readAt: string | null;
  pathway: PathwayResponse | null;
  /**
   * Compteur incrémenté à chaque projection. `ValueAndPropertyDisplay` et `PatientHeaderCard`
   * figent leur valeur au montage : sans remontage sur cette clé, déplacer le curseur ne
   * changerait rien à l'écran.
   */
  version: number;
  /**
   * L'état tenu décrit-il bien le dossier ET LE JOUR demandés ?
   *
   * Dérivé au rendu, pas posé dans un effet, pour qu'il n'existe aucune image où l'écran montre
   * la projection précédente en se croyant à jour.
   *
   * La page temporelle l'ignore volontairement : entre deux crans du curseur, garder les valeurs
   * du cran précédent le temps de l'aller-retour se lit comme une mise à jour, alors que vider la
   * page à chaque cran ferait clignoter le dossier. La modale, elle, l'attend — elle annonce une
   * DATE dans son en-tête, et afficher sous cette date les valeurs d'une autre serait exactement
   * le défaut qu'elle existe pour ne plus avoir.
   */
  ready: boolean;
  /** Re-read the dated state AND the care pathway: a gesture changes both. */
  reload: () => void;
}

interface TemporalStatePayload {
  fields?: Record<string, unknown>;
  field_dates?: Record<string, string>;
  field_evidence?: Record<string, FieldEvidence[]>;
  field_gestures?: Record<string, ServedGesture>;
  clinical_keys?: string[];
  read_at?: string | null;
}

/** L'état daté d'un dossier à la date `at` (`null` = aujourd'hui), et son parcours de soins. */
export function useTemporalProjection(
  tenantId: string | undefined,
  patientId: string | number | undefined,
  at: string | null,
  /** `false` while the caller shows nothing (a closed modal): nothing is read. */
  enabled = true,
): TemporalProjection {
  const [fields, setFields] = useState<Record<string, unknown> | null>(null);
  const [dates, setDates] = useState<Record<string, string>>({});
  const [evidence, setEvidence] = useState<Record<string, FieldEvidence[]>>({});
  const [gestures, setGestures] = useState<Record<string, ServedGesture>>({});
  const [clinicalKeys, setClinicalKeys] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);
  const [readAt, setReadAt] = useState<string | null>(null);
  const [pathway, setPathway] = useState<PathwayResponse | null>(null);
  const [version, setVersion] = useState(0);
  // Quelle lecture a le droit d'écrire dans l'état. Le curseur déclenche une lecture par cran et
  // rien ne garantit l'ordre des réponses : sans jeton, un `/state` du 3 janvier arrivé après celui
  // d'aujourd'hui repeindrait la page avec l'état du 3 janvier.
  const requestToken = useRef(0);
  // Same guard for the pathway: a reload after a gesture and the first read can cross.
  const pathwayToken = useRef(0);
  // Ce que l'état tenu décrit, et ce qu'on lui demande de décrire. L'égalité des deux est `ready`.
  const requestKey = `${enabled ? "on" : "off"}|${tenantId ?? ""}|${patientId ?? ""}|${at ?? ""}`;
  const [settledKey, setSettledKey] = useState<string | null>(null);

  const loadState = useCallback(async () => {
    if (!enabled || !tenantId || patientId === undefined || patientId === null) return;
    const token = ++requestToken.current;
    try {
      const proj = (await API.get({
        path: `/patient/${tenantId}/${patientId}/state`,
        query: at ? { at } : undefined,
      })) as TemporalStatePayload;
      if (requestToken.current !== token) return;
      if (!proj || proj.fields === undefined) throw new Error("empty projection");
      setFields(proj.fields ?? null);
      setDates(proj.field_dates ?? {});
      setEvidence(proj.field_evidence ?? {});
      setGestures(proj.field_gestures ?? {});
      setClinicalKeys(proj.clinical_keys ?? []);
      setReadAt(proj.read_at ?? null);
      setFailed(false);
    } catch (error) {
      if (requestToken.current !== token) return;
      // Affiché, jamais seulement journalisé : observé en vrai, une base en retard de deux
      // migrations faisait lever `/state` et la page rapportait un patient vide.
      console.error("Error loading the temporal projection:", error);
      setFailed(true);
      setFields(null);
      setDates({});
      setEvidence({});
      setGestures({});
      setClinicalKeys([]);
      setReadAt(null);
    } finally {
      if (requestToken.current === token) {
        setSettledKey(requestKey);
        setVersion((value) => value + 1);
      }
    }
  }, [enabled, tenantId, patientId, at, requestKey]);

  // Changer de dossier vide l'écran avant de le remplir. La page patient remonte à chaque patient
  // et ne voyait pas le problème ; la modale du jeu de données, elle, est un composant qui reste
  // monté et que l'on rouvre sur un autre patient — sans ce vidage, la projection du précédent
  // restait affichée sous l'identité du suivant le temps d'un aller-retour.
  useEffect(() => {
    requestToken.current += 1;
    pathwayToken.current += 1;
    setFields(null);
    setDates({});
    setEvidence({});
    setGestures({});
    setClinicalKeys([]);
    setReadAt(null);
    setFailed(false);
    setPathway(null);
  }, [tenantId, patientId]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const loadPathway = useCallback(async () => {
    if (!enabled || !tenantId || patientId === undefined || patientId === null) return;
    const token = ++pathwayToken.current;
    try {
      const res = (await API.get({
        path: `/patient/${tenantId}/${patientId}/pathway`,
      })) as PathwayResponse;
      if (pathwayToken.current === token) setPathway(res ?? null);
    } catch {
      if (pathwayToken.current === token) setPathway(null);
    }
  }, [enabled, tenantId, patientId]);

  useEffect(() => {
    loadPathway();
  }, [loadPathway]);

  // A gesture rebuilds the care pathway on the server: the episode header, its act, the chronology
  // and the gesture each episode row shows all come from it. Reloaded with the dated state, or they
  // kept describing the record as it stood before the gesture until a full reload.
  const reload = useCallback(() => {
    loadState();
    loadPathway();
  }, [loadState, loadPathway]);

  return {
    fields,
    dates,
    evidence,
    gestures,
    clinicalKeys,
    failed,
    readAt,
    pathway,
    version,
    ready: settledKey === requestKey,
    reload,
  };
}
